/**
 * Ticket-Liste je Queue mit Filtern und Volltextsuche (Spec 7.1).
 *
 * Live-Updates: `useTicketEvents` laedt die Liste neu, sobald sich irgendwo im
 * Mandanten ein Ticket aendert. Bei 10–50 Tickets/Monat ist das Neuladen der
 * Seite billiger als jede Delta-Logik (Spec: Einfachheit vor Skalierung).
 */
import {
  isOverdue,
  OPEN_TICKET_STATUSES,
  TICKET_PRIORITIES,
  TICKET_SORTS,
  TICKET_STATUSES,
  type Paginated,
  type Queue,
  type Tag,
  type Team,
  type TicketListItem,
  type TicketPriority,
  type TicketSort,
  type TicketStatus,
} from '@tickethub/shared';
import { AlertTriangle, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useTicketEvents } from '@/lib/realtime';
import { useSession } from '@/lib/session';
import { queuesApi, tagsApi, teamsApi, ticketsApi, type TicketFilters } from '@/lib/tickets';

const PER_PAGE = 20;

export function TicketsPage() {
  const { t, i18n } = useTranslation();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;

  const [queues, setQueues] = useState<Queue[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [page, setPage] = useState<Paginated<TicketListItem> | null>(null);

  const [queueId, setQueueId] = useState('');
  const [status, setStatus] = useState<TicketStatus | 'all' | 'open_only'>('open_only');
  const [priority, setPriority] = useState<TicketPriority | ''>('');
  const [tagId, setTagId] = useState('');
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);

  /**
   * Schnellfilter (Spec 7.1 / 8.1). „Meine Tickets" und „Team-Tickets" sind
   * keine eigenen Endpunkte, sondern Vorbelegungen der bestehenden Filter —
   * `assignee_member_id` bzw. `team_id`. Die Mitglieds-ID kommt aus der
   * Session, das Team aus der Auswahl daneben.
   */
  const [mine, setMine] = useState(false);
  const [teamId, setTeamId] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sort, setSort] = useState<TicketSort>('created_desc');

  const memberId = activeMembership?.member_id ?? null;

  const filters = useMemo<TicketFilters>(() => {
    const statuses =
      status === 'all' ? undefined : status === 'open_only' ? [...OPEN_TICKET_STATUSES] : [status];

    return {
      ...(statuses ? { status: statuses } : {}),
      ...(queueId ? { queue_id: queueId } : {}),
      ...(priority ? { priority } : {}),
      ...(tagId ? { tag_id: tagId } : {}),
      ...(mine && memberId ? { assignee_member_id: memberId } : {}),
      ...(teamId ? { team_id: teamId } : {}),
      ...(overdueOnly ? { overdue: true } : {}),
      ...(submittedSearch ? { q: submittedSearch } : {}),
      sort,
      page: pageNumber,
      per_page: PER_PAGE,
    };
  }, [
    status,
    queueId,
    priority,
    tagId,
    mine,
    memberId,
    teamId,
    overdueOnly,
    sort,
    submittedSearch,
    pageNumber,
  ]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      setPage(await ticketsApi.list(tenantId, filters));
    } catch (error) {
      toastError(error, t);
    }
  }, [tenantId, filters, t]);

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      try {
        const [queueResult, tagResult, teamResult] = await Promise.all([
          queuesApi.list(tenantId),
          tagsApi.list(tenantId),
          teamsApi.list(tenantId),
        ]);
        setQueues(queueResult.items);
        setTags(tagResult.items);
        setTeams(teamResult.items);
      } catch (error) {
        toastError(error, t);
      }
    })();
  }, [tenantId, t]);

  useEffect(() => {
    // `load`/`reload` setzen State erst *nach* dem Netzwerk-Await — es entsteht
    // keine kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load]);

  useTicketEvents(tenantId, () => {
    void load();
  });

  const formatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('ticket.list_title')}</h1>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPageNumber(1);
          setSubmittedSearch(search.trim());
        }}
      >
        <label className="flex-1 min-w-56">
          <span className="mb-1 block text-xs font-medium">{t('ticket.search')}</span>
          <Input
            type="search"
            value={search}
            placeholder={t('ticket.search_placeholder')}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium">{t('ticket.queue')}</span>
          <Select
            value={queueId}
            onChange={(event) => {
              setPageNumber(1);
              setQueueId(event.target.value);
            }}
          >
            <option value="">{t('ticket.all_queues')}</option>
            {queues.map((queue) => (
              <option key={queue.id} value={queue.id}>
                {queue.name}
              </option>
            ))}
          </Select>
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium">{t('ticket.status')}</span>
          <Select
            value={status}
            onChange={(event) => {
              setPageNumber(1);
              setStatus(event.target.value as TicketStatus | 'all' | 'open_only');
            }}
          >
            <option value="open_only">{t('ticket.status_filter_open')}</option>
            <option value="all">{t('ticket.status_filter_all')}</option>
            {TICKET_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`ticket.status_${value}`)}
              </option>
            ))}
          </Select>
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium">{t('ticket.priority')}</span>
          <Select
            value={priority}
            onChange={(event) => {
              setPageNumber(1);
              setPriority(event.target.value as TicketPriority | '');
            }}
          >
            <option value="">{t('ticket.all_priorities')}</option>
            {TICKET_PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {t(`ticket.priority_${value}`)}
              </option>
            ))}
          </Select>
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium">{t('ticket.tags')}</span>
          <Select
            value={tagId}
            onChange={(event) => {
              setPageNumber(1);
              setTagId(event.target.value);
            }}
          >
            <option value="">{t('ticket.all_tags')}</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </Select>
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium">{t('ticket.team')}</span>
          <Select
            value={teamId}
            onChange={(event) => {
              setPageNumber(1);
              setTeamId(event.target.value);
            }}
          >
            <option value="">{t('ticket.all_teams')}</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </Select>
        </label>

        <label>
          <span className="mb-1 block text-xs font-medium">{t('ticket.sort')}</span>
          <Select
            value={sort}
            onChange={(event) => {
              setPageNumber(1);
              setSort(event.target.value as TicketSort);
            }}
          >
            {TICKET_SORTS.map((value) => (
              <option key={value} value={value}>
                {t(`ticket.sort_${value}`)}
              </option>
            ))}
          </Select>
        </label>

        <Button type="submit">
          <Search aria-hidden="true" />
          {t('ticket.apply_filters')}
        </Button>
      </form>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={mine ? 'default' : 'outline'}
          disabled={memberId === null}
          onClick={() => {
            setPageNumber(1);
            setMine((value) => !value);
          }}
        >
          {t('ticket.filter_mine')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={overdueOnly ? 'default' : 'outline'}
          onClick={() => {
            setPageNumber(1);
            setOverdueOnly((value) => !value);
          }}
        >
          <AlertTriangle aria-hidden="true" />
          {t('ticket.filter_overdue')}
        </Button>
      </div>

      {page === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : page.items.length === 0 ? (
        <Alert>
          <AlertDescription>{t('ticket.empty')}</AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('ticket.number')}</th>
                  <th className="px-4 py-2 font-medium">{t('ticket.subject')}</th>
                  <th className="px-4 py-2 font-medium">{t('ticket.contact')}</th>
                  <th className="px-4 py-2 font-medium">{t('ticket.queue')}</th>
                  <th className="px-4 py-2 font-medium">{t('ticket.status')}</th>
                  <th className="px-4 py-2 font-medium">{t('ticket.priority')}</th>
                  <th className="px-4 py-2 font-medium">{t('ticket.due_at')}</th>
                  <th className="px-4 py-2 font-medium">{t('ticket.updated')}</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((ticket) => (
                  <tr key={ticket.id} className="border-t hover:bg-muted/30">
                    <td className="px-4 py-2 font-mono text-xs">{ticket.number}</td>
                    <td className="px-4 py-2">
                      <Link className="font-medium hover:underline" to={`/tickets/${ticket.id}`}>
                        {ticket.subject}
                      </Link>
                      {ticket.tags.length > 0 ? (
                        <span className="ml-2 inline-flex gap-1">
                          {ticket.tags.map((tag) => (
                            <Badge key={tag.id} variant="outline">
                              {tag.name}
                            </Badge>
                          ))}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2">{ticket.contact_display_name}</td>
                    <td className="px-4 py-2">{ticket.queue_name}</td>
                    <td className="px-4 py-2">
                      <Badge variant="secondary">{t(`ticket.status_${ticket.status}`)}</Badge>
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={ticket.priority === 'urgent' ? 'destructive' : 'outline'}>
                        {t(`ticket.priority_${ticket.priority}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      {ticket.due_at === null ? (
                        <span className="text-muted-foreground">–</span>
                      ) : isOverdue(ticket) ? (
                        <Badge variant="destructive">
                          <AlertTriangle aria-hidden="true" className="size-3" />
                          {formatter.format(new Date(ticket.due_at))}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {formatter.format(new Date(ticket.due_at))}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {formatter.format(new Date(ticket.updated_at))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <Button
              variant="outline"
              size="sm"
              disabled={page.page <= 1}
              onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
            >
              {t('pagination.previous')}
            </Button>
            <span className="text-muted-foreground">
              {t('pagination.status', { page: page.page, pages: Math.max(1, page.pages) })}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page.page >= page.pages}
              onClick={() => setPageNumber((current) => current + 1)}
            >
              {t('pagination.next')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
